import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api, API_BASE } from '../../services/api';
import {
    PawPrint, Plus, Search, RefreshCw, MapPin, PenLine, Trash2,
    X, ChevronLeft, ChevronRight, Camera, Heart, Calendar,
    Copy, Check, AlertCircle, QrCode, Clock, Navigation,
    Shield, Sparkles, Info, User, Phone, Eye,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════ */

function calcularEdad(fechaNacimiento) {
    if (!fechaNacimiento) return 'Edad desconocida';
    const hoy = new Date();
    const nac = new Date(fechaNacimiento);
    let años = hoy.getFullYear() - nac.getFullYear();
    let meses = hoy.getMonth() - nac.getMonth();
    if (meses < 0 || (meses === 0 && hoy.getDate() < nac.getDate())) {
        años--;
        meses += 12;
    }
    if (años > 0) return `${años} año${años > 1 ? 's' : ''}`;
    if (meses > 0) return `${meses} mes${meses > 1 ? 'es' : ''}`;
    return 'Menos de 1 mes';
}

function getPhotoUrl(urlImg) {
    if (!urlImg) return null;
    if (urlImg.startsWith('http')) return urlImg;
    const base = API_BASE;
    return `${base.replace(/\/$/, '')}/${urlImg.replace(/^\//, '')}`;
}

function getInitials(nombre) {
    return nombre?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function parseDescripcion(desc) {
    const defaults = {
        raza: '', color: '', tamaño: '', personalidad: '', observaciones: '',
        castrado: '', muerde: '', vacunado: '', medicacion: '', odia: '', otroVital: '',
    };
    if (!desc) return defaults;
    const data = { ...defaults };
    desc.split('.').map(s => s.trim()).filter(s => s).forEach(line => {
        const match = line.match(/^([A-Za-zÁ-ú\s]+):\s*(.*)/);
        if (!match) return;
        const key = match[1].toLowerCase().trim();
        const value = match[2].trim();
        switch (key) {
            case 'raza': data.raza = value; break;
            case 'color': data.color = value; break;
            case 'tamaño': data.tamaño = value; break;
            case 'personalidad': data.personalidad = value; break;
            case 'observaciones': data.observaciones = value; break;
            case 'castrado': data.castrado = value; break;
            case 'muerde': data.muerde = value; break;
            case 'vacunado': data.vacunado = value; break;
            case 'medicación': case 'medicacion': data.medicacion = value; break;
            case 'odia': data.odia = value; break;
            case 'otra información vital': case 'otro vital': case 'otrovital': data.otroVital = value; break;
            default: break;
        }
    });
    return data;
}

function generarDescripcion(q) {
    const parts = [];
    if (q.raza) parts.push(`Raza: ${q.raza}`);
    if (q.color) parts.push(`Color: ${q.color}`);
    if (q.tamaño) parts.push(`Tamaño: ${q.tamaño}`);
    if (q.personalidad) parts.push(`Personalidad: ${q.personalidad}`);
    if (q.castrado) parts.push(`Castrado: ${q.castrado}`);
    if (q.muerde) parts.push(`Muerde: ${q.muerde}`);
    if (q.vacunado) parts.push(`Vacunado: ${q.vacunado}`);
    if (q.medicacion) parts.push(`Medicación: ${q.medicacion}`);
    if (q.odia) parts.push(`Odia: ${q.odia}`);
    if (q.observaciones) parts.push(`Observaciones: ${q.observaciones}`);
    if (q.otroVital) parts.push(`Otra información vital: ${q.otroVital}`);
    return parts.join('. ') + (parts.length ? '.' : '');
}

/* ═══════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
   ═══════════════════════════════════════════════════════════ */

export default function MisMascotas() {
    const { user } = useAuth();
    const { userId } = useParams();
    const fileInputRef = useRef(null);

    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshing, setRefreshing] = useState(false);

    const [modalVincular, setModalVincular] = useState(false);
    const [modalEditar, setModalEditar] = useState(false);
    const [modalUbicaciones, setModalUbicaciones] = useState(false);
    const [modalEliminar, setModalEliminar] = useState(false);
    const [selectedMascota, setSelectedMascota] = useState(null);

    const [toast, setToast] = useState(null);
    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    /* ── Cargar datos ── */
    const fetchData = useCallback(async (showSpinner = true) => {
        if (showSpinner) setLoading(true);
        setError(null);
        try {
            const res = await api.getUserCodes(userId);
            setCodes(res.data?.success ? res.data.data || [] : []);
        } catch (err) {
            setError('No se pudieron cargar tus mascotas. Intentá de nuevo.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [userId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleRefresh = () => { setRefreshing(true); fetchData(false); };

    /* ── Filtrado ── */
    const filteredCodes = codes.filter(c => {
        const term = searchTerm.toLowerCase();
        return (c.mascota?.nombre?.toLowerCase() || '').includes(term) ||
            (c.codigo_unico?.toLowerCase() || '').includes(term);
    });

    /* ── Copiar código ── */
    const [copiedId, setCopiedId] = useState(null);
    const copyCode = (codigo, id) => {
        navigator.clipboard.writeText(codigo).then(() => {
            setCopiedId(id);
            showToast('Código copiado al portapapeles');
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    /* ── Vincular código ── */
    const [vincularInput, setVincularInput] = useState('');
    const [vincularLoading, setVincularLoading] = useState(false);

    const handleVincular = async (e) => {
        e.preventDefault();
        const codigo = vincularInput.trim(); // sin toUpperCase, preserva símbolos
        // Acepta cualquier carácter (excepto espacios), exactamente 8
        if (!/^[^\s]{8}$/.test(codigo)) {
            showToast('El código debe tener 8 caracteres sin espacios', 'error');
            return;
        }
        setVincularLoading(true);
        try {
            const res = await api.vincularCodigo(userId, codigo);
            if (res.data?.success) {
                showToast('¡Código vinculado con éxito!');
                setVincularInput('');
                setModalVincular(false);
                fetchData(false);
            } else {
                showToast(res.data?.error || 'Error al vincular', 'error');
            }
        } catch (err) {
            showToast(err.response?.data?.error || 'Error al vincular código', 'error');
        } finally {
            setVincularLoading(false);
        }
    };

    /* ── Editar mascota — 2 PASOS ── */
    const [editForm, setEditForm] = useState({});
    const [editQuestionario, setEditQuestionario] = useState({
        raza: '', color: '', tamaño: '', personalidad: '', observaciones: '',
        castrado: '', muerde: '', vacunado: '', medicacion: '', odia: '', otroVital: '',
    });
    const [editLoading, setEditLoading] = useState(false);
    const [editPhotoFile, setEditPhotoFile] = useState(null);
    const [editPhotoPreview, setEditPhotoPreview] = useState(null);
    const [editStep, setEditStep] = useState(1);
    const totalSteps = 2;
    const [codigoActivo, setCodigoActivo] = useState(null);

    const calcularProgreso = (form) => {
        let count = 0;
        if (form.nombre?.trim()) count++;
        if (form.fecha_nacimiento) count++;
        if (form.sexo !== null && form.sexo !== '' && form.sexo !== undefined) count++;
        if (form.direccion?.trim()) count++;
        if (form.persona1?.trim()) count++;
        if (form.persona1tel?.trim()) count++;
        if (form.mensajeRescate?.trim()) count++;
        return Math.round((count / 7) * 100);
    };

    const isMascotaValida = (m) => !!m && !!m.id;

    const openEditar = (mascota, code) => {
        const base = mascota || {};
        setCodigoActivo(code || null);
        setSelectedMascota(mascota || { id: null, nombre: '' });
        setEditForm({
            nombre: base.nombre || '',
            fecha_nacimiento: base.fecha_nacimiento || '',
            sexo: base.sexo !== undefined && base.sexo !== null ? base.sexo : '',
            descripcion: base.descripcion || '',
            direccion: base.direccion || '',
            persona1: base.persona1 || '',
            persona1tel: base.persona1tel || '',
            persona1ig: base.persona1ig || '',
            persona2: base.persona2 || '',
            persona2tel: base.persona2tel || '',
            persona2ig: base.persona2ig || '',
            mensajeRescate: base.mensajeRescate || '',
        });
        setEditQuestionario(parseDescripcion(base.descripcion || ''));
        setEditPhotoFile(null);
        setEditPhotoPreview(getPhotoUrl(base.urlImg));
        setEditStep(1);
        setModalEditar(true);
    };

    useEffect(() => {
        if (modalEditar) {
            setEditForm(prev => ({ ...prev, descripcion: generarDescripcion(editQuestionario) }));
        }
    }, [editQuestionario, modalEditar]);

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 6 * 1024 * 1024) {
            showToast('La imagen no puede superar los 6MB', 'error');
            return;
        }
        setEditPhotoFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setEditPhotoPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleEditar = async (e) => {
        e.preventDefault();
        if (!editForm.nombre?.trim()) {
            showToast('El nombre es obligatorio', 'error');
            setEditStep(1);
            return;
        }
        setEditLoading(true);
        try {
            const payload = {
                nombre: editForm.nombre.trim(),
                fecha_nacimiento: editForm.fecha_nacimiento || null,
                sexo: editForm.sexo !== '' ? Number(editForm.sexo) : null,
                direccion: editForm.direccion.trim() || null,
                descripcion: editForm.descripcion.trim() || null,
                persona1: editForm.persona1.trim() || null,
                persona1tel: editForm.persona1tel.trim() || null,
                persona1ig: editForm.persona1ig.trim() || null,
                persona2: editForm.persona2.trim() || null,
                persona2tel: editForm.persona2tel.trim() || null,
                persona2ig: editForm.persona2ig.trim() || null,
                mensajeRescate: editForm.mensajeRescate.trim() || null,
            };

            let mascotaId = selectedMascota?.id;

            if (mascotaId) {
                await api.updateMascota(mascotaId, payload);
            } else {
                const createPayload = {
                    ...payload,
                    id_usuario: userId,
                    codigo_id: codigoActivo?.codigo_id,
                };
                const res = await api.createMascota(createPayload);
                if (res.data?.success === false || !res.data) {
                    throw new Error(res.data?.error || 'No se pudo crear la mascota');
                }
                mascotaId = res.data?.data?.id || res.data?.id;
                if (!mascotaId) throw new Error('El servidor no devolvió el ID de la mascota creada');
            }

            if (editPhotoFile && mascotaId) {
                await api.uploadMascotaFoto(mascotaId, editPhotoFile, { nombre: editForm.nombre });
            }

            showToast(selectedMascota?.id ? 'Mascota actualizada correctamente' : '¡Mascota creada y vinculada!');
            setModalEditar(false);
            setCodigoActivo(null);
            fetchData(false);
        } catch (err) {
            const msg = err.response?.data?.error || err.message || 'Error al guardar';
            showToast(msg, 'error');
        } finally {
            setEditLoading(false);
        }
    };

    /* ── Ubicaciones ── */
    const [ubicaciones, setUbicaciones] = useState([]);
    const [ubicacionesLoading, setUbicacionesLoading] = useState(false);

    const openUbicaciones = async (mascota) => {
        if (!isMascotaValida(mascota)) {
            showToast('Este código todavía no tiene una mascota asociada', 'error');
            return;
        }
        setSelectedMascota(mascota);
        setModalUbicaciones(true);
        setUbicacionesLoading(true);
        try {
            const res = await api.getUbicacionesByMascota(mascota.id);
            const data = res.data?.success ? res.data.data : res.data;
            setUbicaciones(Array.isArray(data) ? data : []);
        } catch (err) {
            showToast('Error al cargar ubicaciones', 'error');
        } finally {
            setUbicacionesLoading(false);
        }
    };

    /* ── Eliminar vínculo ── */
    const [eliminarLoading, setEliminarLoading] = useState(false);
    const handleEliminar = async () => {
        if (!selectedMascota?.usuario_codigo_id) {
            showToast('Error: código inválido', 'error');
            return;
        }
        setEliminarLoading(true);
        try {
            await api.desvincularCodigo(selectedMascota.usuario_codigo_id);
            showToast('Código desvinculado correctamente');
            setModalEliminar(false);
            fetchData(false);
        } catch (err) {
            showToast('Error al desvincular', 'error');
        } finally {
            setEliminarLoading(false);
        }
    };

    /* ═══════════════════════════════════════════════════════════
       RENDER
       ═══════════════════════════════════════════════════════════ */

    return (
        <div className="min-h-screen bg-sp-light mt-16">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-24 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-2xl shadow-lg text-sm font-bold animate-bounce ${toast.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {toast.type === 'success' ? <Sparkles size={16} /> : <AlertCircle size={16} />}
                    {toast.message}
                </div>
            )}

            {/* Hero */}
            <section className="bg-gradient-to-br from-sp-dark to-sp-primary pt-6 pb-12 px-4 xs:px-5 relative overflow-hidden">
                <div className="max-w-[1100px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-white text-xl xs:text-2xl font-black flex items-center gap-2">
                            <PawPrint className="w-7 h-7" /> Mis Mascotas
                        </h1>
                        <p className="text-white/70 text-sm font-semibold mt-1">
                            {codes.length === 0 ? 'Aún no tenés mascotas vinculadas' : `Gestioná ${codes.length} mascota${codes.length > 1 ? 's' : ''}`}
                        </p>
                    </div>
                    <button onClick={() => setModalVincular(true)}
                        className="bg-sp-cta text-white px-5 py-3 rounded-full font-extrabold text-sm shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5 active:scale-95 transition flex items-center gap-2">
                        <Plus size={18} /> Vincular código
                    </button>
                </div>
                <div className="absolute -bottom-0.5 left-0 right-0 h-8 bg-sp-light" style={{ clipPath: 'ellipse(55% 100% at 50% 100%)' }} />
            </section>

            {/* Toolbar */}
            {codes.length > 0 && (
                <div className="max-w-[1100px] mx-auto px-4 xs:px-5 py-4 flex items-center gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-sp-muted w-4 h-4" />
                        <input type="text" placeholder="Buscar por nombre o código..." value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-10 py-2.5 bg-white rounded-xl shadow-sp text-sm font-semibold placeholder:text-sp-muted/60 focus:outline-none focus:ring-2 focus:ring-sp-primary/30" />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-sp-muted hover:text-sp-text">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                    <button onClick={handleRefresh} disabled={refreshing}
                        className={`p-2.5 bg-white rounded-xl shadow-sp text-sp-muted hover:text-sp-primary transition ${refreshing ? 'animate-spin' : ''}`}>
                        <RefreshCw size={18} />
                    </button>
                </div>
            )}

            {/* Contenido principal */}
            <div className="max-w-[1100px] mx-auto px-4 xs:px-5 pb-10">
                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-10 h-10 border-4 border-sp-primary border-t-transparent rounded-full animate-spin mb-4" />
                        <p className="text-sp-muted font-semibold">Cargando tus mascotas...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
                        <p className="text-sp-muted font-semibold mb-4">{error}</p>
                        <button onClick={() => fetchData()} className="bg-sp-primary text-white px-5 py-2.5 rounded-full font-bold shadow-cta hover:-translate-y-0.5 transition">Reintentar</button>
                    </div>
                ) : filteredCodes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-3xl shadow-sp">
                        <PawPrint className="w-16 h-16 text-sp-primary/30 mb-4" />
                        <h3 className="text-sp-text font-black text-lg mb-2">{searchTerm ? 'No se encontraron resultados' : 'Sin mascotas vinculadas'}</h3>
                        <p className="text-sp-muted font-semibold mb-6">{searchTerm ? 'Probá con otro término' : 'Vinculá tu primer código QR'}</p>
                        {!searchTerm && (
                            <button onClick={() => setModalVincular(true)} className="bg-sp-cta text-white px-5 py-2.5 rounded-full font-extrabold shadow-cta hover:-translate-y-0.5 transition flex items-center gap-2">
                                <Plus size={18} /> Vincular código
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-6">
                        {filteredCodes.map((code) => {
                            const m = code.mascota;
                            const hasMascota = isMascotaValida(m);
                            const photoUrl = getPhotoUrl(m?.urlImg);
                            const edad = calcularEdad(m?.fecha_nacimiento);

                            const sexoNum = m?.sexo;
                            const sexoTexto = sexoNum === 0 ? 'Macho' : sexoNum === 1 ? 'Hembra' : null;
                            const sexoSimbolo = sexoNum === 0 ? '♂' : sexoNum === 1 ? '♀' : null;

                            return (
                                <div key={code.id} className="group relative bg-white rounded-2xl shadow-sp hover:shadow-sp-hover transition overflow-hidden">
                                    {/* Badge código */}
                                    <div className="absolute top-3 left-3 z-10">
                                        <button onClick={() => copyCode(code.codigo_unico, code.id)}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-bold text-sp-primary shadow-sm hover:bg-white transition">
                                            <QrCode size={12} /> {code.codigo_unico}
                                            {copiedId === code.id ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
                                        </button>
                                    </div>
                                    {hasMascota && sexoSimbolo && (
                                        <div className={`absolute top-3 right-3 z-10 w-7 h-7 rounded-full flex items-center justify-center text-sm font-black ${sexoNum === 0 ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600'}`}>
                                            {sexoSimbolo}
                                        </div>
                                    )}
                                    {/* Foto */}
                                    <div className="relative h-48 bg-sp-surface flex items-center justify-center overflow-hidden">
                                        {photoUrl ? (
                                            <img src={photoUrl} alt={m?.nombre || ''} className="w-full h-full object-cover"
                                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                        ) : null}
                                        <div className={`absolute inset-0 flex flex-col items-center justify-center text-sp-primary/40 ${photoUrl ? 'hidden' : 'flex'}`}>
                                            {hasMascota ? <><PawPrint size={36} /><span className="text-lg font-black">{getInitials(m.nombre)}</span></> : <QrCode size={36} />}
                                        </div>
                                        {!hasMascota && (
                                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                                <span className="text-white font-black text-sm">Sin datos</span>
                                            </div>
                                        )}
                                    </div>
                                    {/* Cuerpo */}
                                    <div className="p-4">
                                        <h3 className="font-black text-sp-text text-lg truncate">{hasMascota ? m.nombre : 'Sin nombre'}</h3>
                                        {hasMascota ? (
                                            <>
                                                <div className="flex items-center gap-3 mt-1 text-sp-muted text-xs font-semibold">
                                                    <span className="flex items-center gap-1"><Calendar size={12} /> {edad}</span>
                                                    {sexoTexto && <span className="flex items-center gap-1"><Heart size={12} /> {sexoTexto}</span>}
                                                </div>
                                                {m.descripcion && <p className="mt-2 text-sp-body text-sm line-clamp-2">{m.descripcion}</p>}
                                                {m.direccion && (
                                                    <div className="mt-2 flex items-start gap-1 text-sp-muted text-xs">
                                                        <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                                                        <span className="line-clamp-1">{m.direccion}</span>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <p className="mt-2 text-sp-muted text-sm font-semibold">Este código aún no tiene datos</p>
                                        )}
                                        <div className="mt-4 flex gap-2">
                                            <button onClick={() => openUbicaciones(hasMascota ? m : null)}
                                                disabled={!hasMascota}
                                                className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-sp-label text-sp-primary rounded-xl font-bold text-xs hover:bg-sp-surface transition disabled:opacity-50 disabled:cursor-not-allowed">
                                                <MapPin size={14} /> Ubicaciones
                                            </button>
                                            <button onClick={() => openEditar(hasMascota ? m : null, code)}
                                                className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-sp-primary text-sp-primary rounded-xl font-bold text-xs hover:bg-sp-primary hover:text-white transition">
                                                <PenLine size={14} /> {hasMascota ? 'Editar' : 'Cargar datos'}
                                            </button>
                                        </div>
                                    </div>
                                    {/* Botón eliminar (desvincular) */}
                                    <button
                                        onClick={() => {
                                            setSelectedMascota({ ...m, usuario_codigo_id: code.id, codigo_unico: code.codigo_unico });
                                            setModalEliminar(true);
                                        }}
                                        className="absolute top-3 right-3 z-10 p-1.5 bg-white/80 rounded-full text-red-400 hover:text-red-600 hover:bg-white transition opacity-0 group-hover:opacity-100"
                                        title="Desvincular código"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ═══════════════════ MODALES ═══════════════════ */}

            {/* MODAL VINCULAR */}
            {modalVincular && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModalVincular(false)}>
                    <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-black text-lg flex items-center gap-2"><QrCode size={20} /> Vincular código</h3>
                            <button onClick={() => setModalVincular(false)} className="text-sp-muted"><X size={20} /></button>
                        </div>
                        <form onSubmit={handleVincular} className="space-y-4">
                            <p className="text-sp-body text-sm">Ingresá el código de 8 caracteres de la chapita (puede incluir #, @, etc.).</p>
                            <div>
                                <label className="block text-sm font-semibold mb-1">Código QR</label>
                                <input
                                    type="text"
                                    value={vincularInput}
                                    onChange={e => setVincularInput(e.target.value)}   // sin toUpperCase
                                    placeholder="Ej: #@KU9995"
                                    maxLength={8}
                                    className="w-full p-3 border rounded-xl text-center text-lg font-bold tracking-wider"
                                    autoFocus
                                />
                                <span className="text-xs text-sp-muted mt-1 block">8 caracteres (puede contener símbolos)</span>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setModalVincular(false)} className="px-4 py-2 text-sp-muted font-bold">Cancelar</button>
                                <button type="submit" disabled={vincularLoading}
                                    className="px-5 py-2 bg-sp-primary text-white rounded-full font-bold flex items-center gap-2 disabled:opacity-70">
                                    {vincularLoading ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Plus size={16} />}
                                    {vincularLoading ? 'Vinculando...' : 'Vincular'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL EDITAR (2 pasos) */}
            {modalEditar && selectedMascota && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setModalEditar(false)}>
                    <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        {/* Header del modal */}
                        <div className="sticky top-0 bg-white z-10 p-5 pb-3">
                            <div className="flex items-center justify-between mb-3">
                                <button onClick={() => setEditStep(s => Math.max(1, s - 1))} disabled={editStep === 1}
                                    className="p-1.5 rounded-full hover:bg-sp-light disabled:opacity-30"><ChevronLeft size={20} /></button>
                                <div className="text-center">
                                    <h2 className="font-black text-lg">{selectedMascota.id ? `Editar ${selectedMascota.nombre || 'mascota'}` : 'Cargar datos'}</h2>
                                    <div className="text-xs text-sp-muted font-semibold">Paso {editStep}/{totalSteps} · Perfil: {calcularProgreso(editForm)}%</div>
                                </div>
                                <button onClick={() => { setModalEditar(false); setCodigoActivo(null); }} className="p-1.5 rounded-full hover:bg-sp-light"><X size={20} /></button>
                            </div>
                            {/* Barra de progreso */}
                            <div className="h-2 bg-sp-surface rounded-full overflow-hidden">
                                <div className="h-full bg-sp-primary rounded-full transition-all" style={{ width: `${calcularProgreso(editForm)}%` }} />
                            </div>
                            {/* Indicador de pasos */}
                            <div className="flex items-center justify-center gap-2 mt-4">
                                {[1, 2].map((step) => (
                                    <React.Fragment key={step}>
                                        <button onClick={() => setEditStep(step)}
                                            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition ${step === editStep ? 'bg-sp-primary text-white' : step < editStep ? 'bg-green-100 text-green-600' : 'bg-sp-surface text-sp-muted'}`}>
                                            {step === 1 ? <User size={18} /> : <Phone size={18} />}
                                        </button>
                                        {step < 2 && <div className={`h-0.5 w-10 ${step < editStep ? 'bg-green-400' : 'bg-sp-surface'}`} />}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        {/* Body del modal */}
                        <div className="p-5 pt-0 space-y-5">
                            {editStep === 1 && (
                                <div className="space-y-5">
                                    {/* Foto */}
                                    <div className="flex justify-center">
                                        <div className="relative cursor-pointer group" onClick={() => fileInputRef.current?.click()}>
                                            <div className="w-28 h-28 rounded-full bg-sp-surface flex items-center justify-center overflow-hidden border-4 border-white shadow">
                                                {editPhotoPreview ? <img src={editPhotoPreview} className="w-full h-full object-cover" alt="Preview" /> : <Camera size={36} className="text-sp-primary/50" />}
                                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition rounded-full">
                                                    <Camera size={24} className="text-white" />
                                                </div>
                                            </div>
                                            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} hidden />
                                        </div>
                                    </div>

                                    {/* Campos básicos */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold mb-1">Nombre *</label>
                                            <input type="text" value={editForm.nombre} onChange={e => setEditForm({ ...editForm, nombre: e.target.value })} className="w-full p-2.5 border rounded-xl" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold mb-1">Fecha nac.</label>
                                            <input type="date" value={editForm.fecha_nacimiento} onChange={e => setEditForm({ ...editForm, fecha_nacimiento: e.target.value })} className="w-full p-2.5 border rounded-xl" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold mb-1">Sexo</label>
                                            <div className="flex gap-2">
                                                {[{ valor: 0, label: '♂ Macho' }, { valor: 1, label: '♀ Hembra' }].map(opcion => (
                                                    <button key={opcion.valor} type="button"
                                                        onClick={() => setEditForm({ ...editForm, sexo: opcion.valor })}
                                                        className={`flex-1 py-2.5 rounded-xl font-bold text-sm border transition ${editForm.sexo === opcion.valor ? 'bg-sp-primary text-white border-sp-primary' : 'bg-white border-sp-surface'
                                                            }`}>
                                                        {opcion.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold mb-1">Dirección</label>
                                            <input type="text" value={editForm.direccion} onChange={e => setEditForm({ ...editForm, direccion: e.target.value })} className="w-full p-2.5 border rounded-xl" />
                                        </div>
                                    </div>

                                    {/* Características (questionario) */}
                                    <div className="bg-sp-surface/50 rounded-2xl p-4 space-y-3">
                                        <h4 className="font-bold text-sp-text flex items-center gap-1.5"><Info size={16} /> Características</h4>
                                        <div className="grid grid-cols-2 gap-3">
                                            <input type="text" placeholder="Raza" value={editQuestionario.raza} onChange={e => setEditQuestionario({ ...editQuestionario, raza: e.target.value })} className="p-2.5 border rounded-xl" />
                                            <input type="text" placeholder="Color" value={editQuestionario.color} onChange={e => setEditQuestionario({ ...editQuestionario, color: e.target.value })} className="p-2.5 border rounded-xl" />
                                            <select value={editQuestionario.tamaño} onChange={e => setEditQuestionario({ ...editQuestionario, tamaño: e.target.value })} className="p-2.5 border rounded-xl col-span-2">
                                                <option value="">Tamaño</option>
                                                <option>Pequeño</option><option>Mediano</option><option>Grande</option>
                                            </select>
                                            <input type="text" placeholder="Personalidad" value={editQuestionario.personalidad} onChange={e => setEditQuestionario({ ...editQuestionario, personalidad: e.target.value })} className="p-2.5 border rounded-xl col-span-2" />
                                        </div>
                                    </div>

                                    {/* Datos vitales */}
                                    <div className="space-y-3">
                                        <h4 className="font-bold text-sp-text flex items-center gap-1.5"><Shield size={16} /> Datos vitales</h4>
                                        {[
                                            { key: 'castrado', label: '¿Castrado?', options: ['Sí', 'No'] },
                                            { key: 'vacunado', label: '¿Vacunado?', options: ['Sí, al día', 'No'] },
                                        ].map(({ key, label, options }) => (
                                            <div key={key} className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm">
                                                <span className="font-semibold text-sm">{label}</span>
                                                <div className="flex gap-2">
                                                    {options.map(opt => (
                                                        <button key={opt} onClick={() => setEditQuestionario({ ...editQuestionario, [key]: opt })}
                                                            className={`px-3 py-1.5 rounded-full text-xs font-bold ${editQuestionario[key] === opt ? 'bg-sp-primary text-white' : 'bg-sp-surface text-sp-muted'}`}>{opt}</button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                        <div className="bg-white rounded-xl p-3 shadow-sm">
                                            <span className="font-semibold text-sm">¿Muerde?</span>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {['No, amigable', 'Puede morder si se asusta', 'Sí, agresivo'].map(opt => (
                                                    <button key={opt} onClick={() => setEditQuestionario({ ...editQuestionario, muerde: opt })}
                                                        className={`px-3 py-1.5 rounded-full text-xs font-bold ${editQuestionario.muerde === opt ? 'bg-sp-primary text-white' : 'bg-sp-surface text-sp-muted'}`}>{opt}</button>
                                                ))}
                                            </div>
                                        </div>
                                        <input type="text" placeholder="Medicación regular" value={editQuestionario.medicacion} onChange={e => setEditQuestionario({ ...editQuestionario, medicacion: e.target.value })} className="w-full p-2.5 border rounded-xl" />
                                        <input type="text" placeholder="Odia alguna acción o situación" value={editQuestionario.odia} onChange={e => setEditQuestionario({ ...editQuestionario, odia: e.target.value })} className="w-full p-2.5 border rounded-xl" />
                                        <textarea placeholder="Observaciones" value={editQuestionario.observaciones} onChange={e => setEditQuestionario({ ...editQuestionario, observaciones: e.target.value })} className="w-full p-2.5 border rounded-xl" rows={2} />
                                        <textarea placeholder="Otra información vital" value={editQuestionario.otroVital} onChange={e => setEditQuestionario({ ...editQuestionario, otroVital: e.target.value })} className="w-full p-2.5 border rounded-xl" rows={2} />
                                    </div>
                                </div>
                            )}

                            {editStep === 2 && (
                                <div className="space-y-5">
                                    <h4 className="font-bold text-sp-text flex items-center gap-1.5"><Phone size={16} /> Contactos de emergencia</h4>
                                    {/* Contacto 1 */}
                                    <div className="border rounded-2xl p-4 space-y-3">
                                        <div className="flex items-center gap-2 font-bold text-sm"><span className="w-6 h-6 rounded-full bg-sp-primary text-white flex items-center justify-center text-xs">1</span> Principal</div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <input type="text" placeholder="Nombre" value={editForm.persona1} onChange={e => setEditForm({ ...editForm, persona1: e.target.value })} className="p-2.5 border rounded-xl" />
                                            <input type="tel" placeholder="Teléfono" value={editForm.persona1tel} onChange={e => setEditForm({ ...editForm, persona1tel: e.target.value })} className="p-2.5 border rounded-xl" />
                                            <input type="text" placeholder="Instagram" value={editForm.persona1ig} onChange={e => setEditForm({ ...editForm, persona1ig: e.target.value })} className="p-2.5 border rounded-xl col-span-2" />
                                        </div>
                                    </div>
                                    {/* Contacto 2 */}
                                    <div className="border rounded-2xl p-4 space-y-3">
                                        <div className="flex items-center gap-2 font-bold text-sm"><span className="w-6 h-6 rounded-full bg-sp-pink text-white flex items-center justify-center text-xs">2</span> Secundario</div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <input type="text" placeholder="Nombre" value={editForm.persona2} onChange={e => setEditForm({ ...editForm, persona2: e.target.value })} className="p-2.5 border rounded-xl" />
                                            <input type="tel" placeholder="Teléfono" value={editForm.persona2tel} onChange={e => setEditForm({ ...editForm, persona2tel: e.target.value })} className="p-2.5 border rounded-xl" />
                                            <input type="text" placeholder="Instagram" value={editForm.persona2ig} onChange={e => setEditForm({ ...editForm, persona2ig: e.target.value })} className="p-2.5 border rounded-xl col-span-2" />
                                        </div>
                                    </div>
                                    {/* Mensaje de rescate */}
                                    <div>
                                        <label className="block font-bold text-sp-text mb-2"><Shield size={16} className="inline mr-1" /> Mensaje de rescate</label>
                                        <textarea value={editForm.mensajeRescate} onChange={e => setEditForm({ ...editForm, mensajeRescate: e.target.value })}
                                            className="w-full p-3 border rounded-xl" rows={5} placeholder="Escribí un mensaje claro y emotivo..." />
                                        <div className="mt-2 p-3 bg-sp-surface/50 rounded-xl border-l-4 border-sp-primary">
                                            <div className="flex items-center gap-1 text-xs font-semibold text-sp-primary mb-1"><Eye size={14} /> Vista previa</div>
                                            <p className="text-sm text-sp-text">{editForm.mensajeRescate || 'Aún no has escrito un mensaje de rescate. Este texto se mostrará cuando alguien escanee el QR de tu mascota.'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer del modal */}
                        <div className="sticky bottom-0 bg-white p-5 border-t flex justify-between">
                            <button onClick={() => setEditStep(s => s - 1)} disabled={editStep === 1}
                                className="px-4 py-2 text-sp-muted font-bold disabled:opacity-30">Anterior</button>
                            {editStep < 2 ? (
                                <button onClick={() => setEditStep(s => s + 1)} className="px-5 py-2 bg-sp-primary text-white rounded-full font-bold flex items-center gap-1">
                                    Siguiente <ChevronRight size={16} />
                                </button>
                            ) : (
                                <button onClick={handleEditar} disabled={editLoading}
                                    className="px-5 py-2 bg-sp-primary text-white rounded-full font-bold flex items-center gap-2 disabled:opacity-70">
                                    {editLoading ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Check size={16} />}
                                    {editLoading ? 'Guardando...' : 'Guardar cambios'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL UBICACIONES */}
            {modalUbicaciones && selectedMascota && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModalUbicaciones(false)}>
                    <div className="bg-white rounded-3xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-5 border-b">
                            <h3 className="font-black text-lg flex items-center gap-2"><Navigation size={20} /> Ubicaciones de {selectedMascota.nombre}</h3>
                            <button onClick={() => setModalUbicaciones(false)} className="text-sp-muted"><X size={20} /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            {ubicacionesLoading ? (
                                <div className="flex flex-col items-center py-10"><div className="animate-spin w-8 h-8 border-4 border-sp-primary border-t-transparent rounded-full mb-4" /><p className="text-sp-muted">Cargando...</p></div>
                            ) : ubicaciones.length === 0 ? (
                                <div className="text-center py-10"><MapPin className="w-12 h-12 text-sp-muted mx-auto mb-4" /><p className="text-sp-muted font-semibold">Sin ubicaciones</p></div>
                            ) : (
                                <div className="space-y-3">
                                    {ubicaciones.map((u, i) => (
                                        <div key={u.id || i} className="flex gap-3 p-3 bg-sp-light rounded-xl">
                                            <div className="flex-shrink-0 mt-1"><MapPin size={18} className="text-sp-primary" /></div>
                                            <div>
                                                <p className="font-bold text-sp-text">{u.lugar || 'Sin dirección'}</p>
                                                <p className="text-xs text-sp-muted flex items-center gap-1 mt-1"><Clock size={12} /> {u.fecha_hora ? new Date(u.fecha_hora).toLocaleString('es-AR') : 'Fecha desconocida'}</p>
                                                {u.ubicacion && (
                                                    <a href={`https://www.google.com/maps?q=${u.ubicacion}`} target="_blank" rel="noreferrer"
                                                        className="text-xs text-sp-primary font-semibold flex items-center gap-1 mt-1 hover:underline"><Navigation size={12} /> Ver en mapa</a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL ELIMINAR */}
            {modalEliminar && selectedMascota && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setModalEliminar(false)}>
                    <div className="bg-white rounded-3xl w-full max-w-sm p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-black text-lg flex items-center gap-2"><Trash2 size={20} /> Desvincular código</h3>
                            <button onClick={() => setModalEliminar(false)} className="text-sp-muted"><X size={20} /></button>
                        </div>
                        <p className="text-sp-body mb-2">¿Estás seguro de desvincular <strong>{selectedMascota.codigo_unico}</strong>?</p>
                        <p className="text-xs text-sp-muted mb-6">Los datos no se eliminarán, pero el código dejará de estar asociado a tu cuenta.</p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setModalEliminar(false)} className="px-4 py-2 text-sp-muted font-bold">Cancelar</button>
                            <button onClick={handleEliminar} disabled={eliminarLoading}
                                className="px-5 py-2 bg-red-500 text-white rounded-full font-bold flex items-center gap-2 disabled:opacity-70">
                                {eliminarLoading ? <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Trash2 size={16} />}
                                {eliminarLoading ? 'Procesando...' : 'Desvincular'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}